import React, { useEffect, useState } from 'react';
import GreenButton from './GreenButton';
import Reveal from './Reveal';

interface Project {
  id: string;
  title: string;
  /** What we made. */
  lead: string;
  body: string;
  to: string;
  cta: string;
  image: string;
  caseStudy?: string;
}

interface Group {
  label: string;
  items: Project[];
}

/**
 * Recent work per department. Each project has a Read more link plus a featured product link.
 */
const groups: Group[] = [
  {
    label: 'Signage',
    items: [
      {
        id: 'kearns-rouse',
        title: 'Kearns & Rouse',
        lead: 'Signboard and projecting sign.',
        body: 'A fascia sign plus a double-sided projecting sign, so the shopfront reads from across the street and along the pavement.',
        to: '/services/projecting-signs',
        cta: 'Projecting signs',
        image: '/home/signage/projecting-kearns.jpg',
      },
      {
        id: 'ijr',
        title: 'IJR',
        lead: 'Custom signboards.',
        body: 'Printed panels made to size for the unit, designed in-house and installed by our team.',
        to: '/services/signboards',
        cta: 'Signboards',
        image: '/home/signage/signboard-ijr.jpg',
      },
      {
        id: 'hodgehill',
        title: 'Hodgehill',
        lead: 'Etched window privacy film.',
        body: 'Frosted film cut to shape with the brand, adding privacy without losing natural light.',
        to: '/services/window-privacy-film',
        cta: 'Privacy film',
        image: '/home/signage/etch-hodghill.jpg',
      },
    ],
  },
  {
    label: 'Vehicles',
    items: [
      {
        id: 'wfk',
        title: 'WFK',
        lead: 'Van signwriting.',
        body: 'Cut and printed vinyl applied to the van panels, turning a work vehicle into a moving advert.',
        to: '/services/vehicle-signwriting',
        cta: 'Vehicle signwriting',
        image: '/home/vehicles/wfk.jpg',
      },
      {
        id: 'shire-structures',
        title: 'Shire Structures',
        lead: 'Fleet graphics.',
        body: 'Consistent branding across the fleet so every vehicle on site looks like part of the same team.',
        to: '/services/vehicle-signwriting',
        cta: 'Vehicle signwriting',
        image: '/home/vehicles/shire-structures.jpg',
      },
      {
        id: 'paisley-flour',
        title: 'Paisley Flour',
        lead: 'Magnetic vehicle panels.',
        body: 'Removable printed magnetics for a car that doubles as a delivery vehicle.',
        to: '/services/magnetic-panels',
        cta: 'Magnetic panels',
        image: '/home/vehicles/magnetic-paisley.jpg',
      },
    ],
  },
  {
    label: 'Clothing',
    items: [
      {
        id: 'clothing',
        title: 'Custom clothing',
        lead: 'Printed and embroidered workwear.',
        body: 'Premium garments customised in-house, from a single hoodie to a full team uniform.',
        to: '/clothing',
        cta: 'Browse clothing',
        image: '/home/hero/clothing.jpg',
      },
    ],
  },
  {
    label: 'Printing',
    items: [
      {
        id: 'green-room',
        title: 'The Green Room Café',
        lead: 'Menus, loyalty cards, table talkers and more.',
        body: 'A cohesive print identity for a Kidderminster café, from folded menus to a pavement A-board.',
        to: '/printing',
        cta: 'Print products',
        image: '/home/hero/printing.jpg',
        caseStudy: 'green-room-cafe',
      },
    ],
  },
  {
    label: 'Display',
    items: [
      {
        id: 'curry-king',
        title: 'Curry King',
        lead: 'Branded gazebo.',
        body: 'A printed canopy and valance that turns a market pitch into a recognisable stall.',
        to: '/services/gazebos',
        cta: 'Gazebos',
        image: '/home/gazebos/curry-king-gazebo.jpg',
      },
      {
        id: 'wildjac',
        title: 'Wildjac',
        lead: 'Gazebo and parasols.',
        body: 'Matching gazebo and parasols so the outdoor seating carries the brand as well as the bar does.',
        to: '/services/parasols',
        cta: 'Parasols',
        image: '/home/gazebos/wildjac-gazebo.jpg',
      },
      {
        id: 'outpost-flag',
        title: 'Outpost Custom',
        lead: 'Feather flag.',
        body: 'Our own forecourt flag, printed and finished in-house.',
        to: '/services/feather-flags',
        cta: 'Feather flags',
        image: '/home/display/oc-flag.jpg',
      },
    ],
  },
];

const allProjects = groups.flatMap((g) => g.items);

// The fallback applies until the team writes a case study for that project in the admin, whose slug then goes in caseStudy.
const readMoreTo = (p: Project) => (p.caseStudy ? `/case-study/${p.caseStudy}` : '/blog?category=case-studies');

const ServicesShowcase: React.FC = () => {
  const [activeId, setActiveId] = useState(allProjects[0].id);
  const active = allProjects.find((s) => s.id === activeId) ?? allProjects[0];

  // Warm the cache so switching tabs never shows a black panel while the photo loads.
  useEffect(() => {
    allProjects.forEach((s) => {
      const img = new Image();
      img.src = s.image;
    });
  }, []);

  return (
    <section id="our-work" className="bg-white text-black py-20 md:py-24 px-6">
      <div className="max-w-6xl mx-auto">
        <Reveal>
          <div className="text-center">
            <h2 className="font-montserrat font-extrabold tracking-[-0.05em] leading-none text-[clamp(30px,4vw,54px)] text-[#78BE20]">
              Recognise some of our recent projects?
            </h2>
            <p className="font-poppins mt-6 text-sm md:text-base text-black">
              Proudly supporting local and national customers at any point of their journey.
            </p>
            <img src="/home/scribble-green.png" alt="" aria-hidden="true" className="mx-auto mt-5 w-40 md:w-56 h-auto" loading="lazy" />
          </div>
        </Reveal>

        <Reveal delay={100} className="mt-12 grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-8 lg:gap-12">
          {/* Project list: chips on mobile, vertical tabs on desktop */}
          <nav
            aria-label="Recent projects"
            className="flex lg:flex-col gap-2 lg:gap-6 overflow-x-auto lg:overflow-visible no-scrollbar -mx-6 px-6 lg:mx-0 lg:px-0 snap-x"
          >
            {groups.map((g) => (
              <div key={g.label} className="flex lg:flex-col gap-2 lg:gap-1 shrink-0">
                <span className="hidden lg:block font-poppins text-[10px] font-semibold tracking-[0.2em] uppercase text-black/40 mb-1">
                  {g.label}
                </span>
                {g.items.map((s) => {
                  const isActive = s.id === activeId;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setActiveId(s.id)}
                      onMouseEnter={() => setActiveId(s.id)}
                      onFocus={() => setActiveId(s.id)}
                      aria-pressed={isActive}
                      className={`snap-start shrink-0 text-left font-montserrat font-bold text-[13px] lg:text-[15px] whitespace-nowrap transition-colors duration-200
                        px-4 py-2 lg:px-0 lg:py-1.5 lg:pl-4 lg:border-l-2
                        ${isActive
                          ? 'bg-[#78BE20] text-white lg:bg-transparent lg:text-[#78BE20] lg:border-[#78BE20]'
                          : 'bg-black/5 text-black/70 lg:bg-transparent lg:border-transparent hover:text-black'}`}
                    >
                      {s.title}
                    </button>
                  );
                })}
              </div>
            ))}
          </nav>

          {/* Panel */}
          <div className="relative overflow-hidden bg-black text-white min-h-[420px] lg:min-h-[520px] flex items-end">
            <img
              key={active.id}
              src={active.image}
              alt={active.title}
              className="absolute inset-0 w-full h-full object-cover animate-fadeIn"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/10" />
            <div key={`${active.id}-copy`} className="relative z-10 p-8 md:p-10 max-w-xl animate-fadeUp">
              <h3 className="font-montserrat font-extrabold text-2xl md:text-[32px] tracking-tight leading-tight mb-3">
                {active.title}
              </h3>
              <p className="font-poppins font-semibold text-sm md:text-base mb-2">{active.lead}</p>
              <p className="font-poppins text-sm leading-6 text-white/80 mb-6">{active.body}</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <GreenButton to={readMoreTo(active)} className="w-full sm:w-auto justify-center">Read more…</GreenButton>
                <GreenButton to={active.to} className="w-full sm:w-auto justify-center">{active.cta}</GreenButton>
              </div>
            </div>
          </div>
        </Reveal>

        <p className="mt-14 text-center font-montserrat font-extrabold text-[#78BE20] text-lg md:text-xl leading-snug">
          Let&apos;s chat about your next project.{' '}
          <a href="tel:01562227117" className="hover:underline whitespace-nowrap">
            Call or WhatsApp 01562 227 117
          </a>
        </p>
      </div>
    </section>
  );
};

export default ServicesShowcase;
